import type { ReactNode } from 'react';
import { PAPIERS_DE_FICHE, type Canal, type PapierDeFiche } from '@nextilt/commun';
import type { Bulle, ChoixDePriorite, Etat, Maillon } from '../partage/api/types';
import { CasesDeLaDuree } from './CasesDeLaDuree';
import { ChampsQuand } from './ChampsQuand';
import { ChoixEntoures } from './ChoixEntoures';
import { EtoilesDeDifficulte } from './EtoilesDeDifficulte';
import { dateDuPlan, motsDesReglages } from './ficheEnMots';
import { HabitDeLaBulle, type HabitDuProjetPourUneBulle } from './HabitDeLaBulle';
import type { TypeEtPapierDeLaFiche } from './HautDeLaFiche';
import { LigneQuiSOuvre, Texte } from './Saisies';
import {
  AU_PLUS_TOT,
  AU_PLUS_TOT_CALCULE,
  AUX_HEURES_D_OUVERTURE,
  C_EST,
  CHOIX_DE_PRIORITE,
  commeLeProjet,
  CONTACT_DE_LA_LIGNE,
  cransDeCanal,
  DIFFICULTE_COURT,
  difficulteExpliquee,
  DUREE,
  E_MAIL,
  FICHE_PAS_ECRITE,
  IDEE_EXPLIQUEE,
  IMPORTANCE,
  LA_FICHE,
  LIEN,
  LIEU,
  MOTS_DES_INTERCALAIRES,
  MOTS_DES_PAPIERS_DE_FICHE,
  MOTS_DES_TYPES_DE_FICHE,
  OU,
  PAPIER_DE_FICHE,
  papierDuType,
  PLUS_SON_E_MAIL,
  PLUS_UN_CONTACT,
  PLUS_UN_LIEN,
  PLUS_UN_LIEU,
  REGLAGES_DE_LA_FICHE,
  UNE_IDEE,
  UNE_TACHE,
} from '../partage/vocabulaire';
import './PanneauDesReglages.css';

/** Les quatre intercalaires du panneau, dans l'ordre. */
export type Intercalaire = keyof typeof MOTS_DES_INTERCALAIRES;
const INTERCALAIRES = Object.keys(MOTS_DES_INTERCALAIRES) as Intercalaire[];

/** L'importance, de la plus forte à la plus douce (maquette : Urgent, Important, Quand tu veux). */
const IMPORTANCES = CHOIX_DE_PRIORITE.filter((c): c is [ChoixDePriorite, string] => c[0] !== null).reverse();

/**
 * **Le panneau Réglages de la fiche de l'ordinateur** (chantier reglages-de-la-fiche,
 * modification 02 et sa deuxième passe ; maquettes `panneau-reglages.html`, proposition B, et
 * `boutons-des-reglages.html`, façon 4). « ⚙ Réglages » retourne la fiche : le panneau prend sa
 * place, sur le même papier, sans boîte posée dessus. Une seule tête, « ‹ La fiche » (ou Échap), le
 * titre écrit à la main et le projet en petit ; quatre onglets à plat, soulignés ; dessous des
 * lignes, le nom à gauche, les choix à droite, tous visibles, le choisi entouré au crayon
 * (`ChoixEntoures`) :
 *
 * - **État** : C'est (une tâche, une idée), l'importance, la durée (les deux cases), la difficulté
 *   (les étoiles), « Où ». Pas de ligne « Où en est-elle » : l'état change par les gestes du pied,
 *   qui gardent l'état d'avant pour « Annuler » (`la-fiche`, décision 1) ;
 * - **Dates** : l'échéance, le rendez-vous, la réponse du monde (`ChampsQuand`, toutes ses lignes),
 *   la date au plus tôt que le serveur donne ;
 * - **Contact** : le contact (nom, numéro), son e-mail, le lieu, le lien ;
 * - **Visuel** : le papier, sur une Classique seulement (une Note, une Contact, une Plan portent
 *   celui de leur type), et l'habit de la bulle sur ses lignes (`HabitDeLaBulle`, `enLignes`).
 *
 * Vide, une ligne dit « + … » en pointillé et ouvre sa saisie sur la ligne. Un réglage changé
 * s'écrit aussitôt et se retrouve en revenant à la fiche. Le même panneau pour les quatre types.
 */
export function PanneauDesReglages({
  bulle,
  chemin,
  intercalaire,
  onIntercalaire,
  onRevenir,
  onChanger,
  typeEtPapier,
  habitDuProjet,
}: {
  bulle: Bulle;
  chemin: Maillon[];
  intercalaire: Intercalaire;
  onIntercalaire: (intercalaire: Intercalaire) => void;
  onRevenir: () => void;
  onChanger: (champs: Partial<Bulle>) => void;
  typeEtPapier?: TypeEtPapierDeLaFiche;
  habitDuProjet?: HabitDuProjetPourUneBulle;
}) {
  /* Le projet, en petit sous le titre ; rien sur la fiche du projet lui-même. */
  const projet = chemin.length > 1 ? (chemin[0]?.titre ?? null) : null;

  return (
    <section className="intercalaires" aria-label={REGLAGES_DE_LA_FICHE}>
      <button type="button" className="intercalaires-retour" onClick={onRevenir}>
        ‹ {LA_FICHE}
      </button>
      <h2 className="intercalaires-titre">{bulle.titre}</h2>
      {projet && <p className="intercalaires-projet">{projet}</p>}

      <div className="intercalaires-onglets" role="tablist" aria-label={REGLAGES_DE_LA_FICHE}>
        {INTERCALAIRES.map((i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === intercalaire}
            data-intercalaire={i}
            className={i === intercalaire ? 'est-choisi' : ''}
            onClick={() => onIntercalaire(i)}
          >
            {MOTS_DES_INTERCALAIRES[i]}
          </button>
        ))}
      </div>

      <div className="intercalaires-page" role="tabpanel" aria-label={MOTS_DES_INTERCALAIRES[intercalaire]}>
        {intercalaire === 'etat' && <PageEtat bulle={bulle} onChanger={onChanger} />}
        {intercalaire === 'dates' && <PageDates bulle={bulle} onChanger={onChanger} />}
        {intercalaire === 'contact' && <PageContact bulle={bulle} onChanger={onChanger} />}
        {intercalaire === 'visuel' && (
          <>
            {typeEtPapier && <LigneDuPapier fiche={typeEtPapier} />}
            {habitDuProjet && <HabitDeLaBulle bulle={bulle} projet={habitDuProjet} enLignes />}
          </>
        )}
      </div>
    </section>
  );
}

/** Une ligne du panneau : le nom à gauche, ce qui se règle à droite (`.ligne` de la maquette). */
function Ligne({ nom, tuto, titre, children }: { nom: string; tuto?: string; titre?: string; children: ReactNode }) {
  return (
    <div className="intercalaires-ligne" data-tuto={tuto} title={titre}>
      <span className="intercalaires-nom">{nom}</span>
      <div className="intercalaires-valeur">{children}</div>
    </div>
  );
}

type SurLaBulle = { bulle: Bulle; onChanger: (champs: Partial<Bulle>) => void };

/**
 * L'intercalaire État. « Une idée », c'est l'état « En réflexion » (`sans_objet`) : elle reste dans
 * le projet mais ne remonte pas dans Maintenant ; « Une tâche » la remet à faire. Une bulle acquise
 * ou en instruction est une tâche. L'importance, « Où » et la difficulté se retirent en recliquant
 * le choix entouré, comme partout dans la fiche : rien n'est obligatoire.
 */
function PageEtat({ bulle, onChanger }: SurLaBulle) {
  const idee = bulle.etat === 'sans_objet';
  const choisirLaNature = (nature: 'tache' | 'idee') => {
    const etat: Etat | null = nature === 'idee' ? (idee ? null : 'sans_objet') : idee ? 'a_faire' : null;
    if (etat) onChanger({ etat });
  };
  /* « Où » tient deux questions séparées : d'où (un des canaux), et aux heures d'ouverture. */
  const ou: [Canal | 'heures', string][] = [...cransDeCanal(), ['heures', AUX_HEURES_D_OUVERTURE]];
  const choisirOu = (cle: Canal | 'heures') => {
    if (cle === 'heures') onChanger({ heuresOuvrables: bulle.heuresOuvrables === true ? null : true });
    else onChanger({ canal: bulle.canal === cle ? null : cle });
  };
  return (
    <div data-tuto="fiche-parametres">
      <Ligne nom={C_EST}>
        <ChoixEntoures
          nom={C_EST}
          choix={[
            { cle: 'tache', mot: UNE_TACHE },
            { cle: 'idee', mot: UNE_IDEE, titre: IDEE_EXPLIQUEE, tuto: 'fiche-idee' },
          ]}
          choisi={idee ? 'idee' : 'tache'}
          onChoisir={choisirLaNature}
        />
      </Ligne>
      <Ligne nom={IMPORTANCE} titre={motsDesReglages(bulle).importanceVient ?? undefined}>
        <ChoixEntoures
          nom={IMPORTANCE}
          choix={IMPORTANCES.map(([cle, mot]) => ({ cle, mot }))}
          choisi={bulle.prioriteChoisie}
          onChoisir={(p) => onChanger({ prioriteChoisie: p === bulle.prioriteChoisie ? null : p })}
        />
      </Ligne>
      <Ligne nom={DUREE}>
        <CasesDeLaDuree effortMin={bulle.effortMin} onEcrire={(effortMin) => onChanger({ effortMin })} />
      </Ligne>
      <Ligne nom={DIFFICULTE_COURT} tuto="fiche-difficulte" titre={difficulteExpliquee(bulle.charge)}>
        <EtoilesDeDifficulte valeur={bulle.charge} onChoisir={(charge) => onChanger({ charge })} />
      </Ligne>
      <Ligne nom={OU}>
        <ChoixEntoures
          nom={OU}
          choix={ou.map(([cle, mot]) => ({ cle, mot }))}
          choisi={[bulle.canal, bulle.heuresOuvrables === true ? 'heures' : null]}
          onChoisir={choisirOu}
        />
      </Ligne>
    </div>
  );
}

/**
 * L'intercalaire Dates : les trois temps de `ChampsQuand`, toutes leurs lignes (vide, « + … » en
 * pointillé), puis la date au plus tôt, qui se lit sans se régler : le serveur la calcule.
 */
function PageDates({ bulle, onChanger }: SurLaBulle) {
  return (
    <>
      <ChampsQuand bulle={bulle} onChanger={onChanger} toutes />
      <Ligne nom={AU_PLUS_TOT}>
        <span className="intercalaires-lu">
          {dateDuPlan(bulle.dateAuPlusTot)}
          <small>{AU_PLUS_TOT_CALCULE}</small>
        </span>
      </Ligne>
    </>
  );
}

/**
 * L'intercalaire Contact : le contact (qui, et son numéro), son e-mail, le lieu, le lien. Une ligne
 * posée se lit, et s'ouvre au clic ; vide, « + … ». Chaque champ s'écrit en se quittant (`Texte`).
 */
function PageContact({ bulle, onChanger }: SurLaBulle) {
  const qui = bulle.contactNom ?? bulle.contactTel;
  return (
    <>
      <LigneQuiSOuvre
        nom={CONTACT_DE_LA_LIGNE}
        valeur={qui}
        vide=""
        mention={bulle.contactNom && bulle.contactTel ? bulle.contactTel : ''}
        action=""
        plus={PLUS_UN_CONTACT}
        tuto="ajouter-contact"
      >
        <span className="intercalaires-deux">
          <Texte valeur={bulle.contactNom} place="Qui" auto onChanger={(contactNom) => onChanger({ contactNom })} />
          <Texte valeur={bulle.contactTel} place="Son numéro" onChanger={(contactTel) => onChanger({ contactTel })} />
        </span>
      </LigneQuiSOuvre>
      <LigneQuiSOuvre nom={E_MAIL} valeur={bulle.contactEmail} vide="" mention="" action="" plus={PLUS_SON_E_MAIL}>
        <Texte valeur={bulle.contactEmail} place="nom@exemple.fr" auto onChanger={(contactEmail) => onChanger({ contactEmail })} />
      </LigneQuiSOuvre>
      <LigneQuiSOuvre nom={LIEU} valeur={bulle.lieuTexte} vide="" mention="" action="" plus={PLUS_UN_LIEU} tuto="ajouter-adresse">
        <Texte valeur={bulle.lieuTexte} place="Une adresse, un guichet…" auto onChanger={(lieuTexte) => onChanger({ lieuTexte })} />
      </LigneQuiSOuvre>
      <LigneQuiSOuvre nom={LIEN} valeur={bulle.lien} vide="" mention="" action="" plus={PLUS_UN_LIEN} tuto="ajouter-lien">
        <Texte valeur={bulle.lien} place="https://…" auto onChanger={(lien) => onChanger({ lien })} />
      </LigneQuiSOuvre>
    </>
  );
}

/**
 * Le papier d'une fiche Classique, entouré : les quatre papiers, et « Comme le projet » quand la
 * bulle a son propre choix. Un choix s'écrit aussitôt. Une Note, une Contact, une Plan : une phrase
 * dit que le type le fixe ; le papier choisi reste en base et revient si la fiche repasse en
 * Classique.
 */
function LigneDuPapier({ fiche }: { fiche: TypeEtPapierDeLaFiche }) {
  const { montree, enBase, duProjet, pasEcrit, onChoisir } = fiche;
  const choix: { cle: PapierDeFiche | 'projet'; mot: string }[] = [
    ...(enBase.papierDeFiche !== null
      ? [{ cle: 'projet' as const, mot: commeLeProjet(MOTS_DES_PAPIERS_DE_FICHE[duProjet.papier]) }]
      : []),
    ...PAPIERS_DE_FICHE.map((p) => ({ cle: p, mot: MOTS_DES_PAPIERS_DE_FICHE[p] })),
  ];
  return (
    <Ligne nom={PAPIER_DE_FICHE}>
      {montree.type !== 'classique' ? (
        <p className="intercalaires-phrase">{papierDuType(MOTS_DES_TYPES_DE_FICHE[montree.type])}</p>
      ) : (
        <ChoixEntoures
          nom={PAPIER_DE_FICHE}
          choix={choix}
          choisi={montree.papier}
          onChoisir={(p) => onChoisir({ papierDeFiche: p === 'projet' ? null : p })}
        />
      )}
      {pasEcrit && (
        <p className="intercalaires-phrase" role="alert">
          {FICHE_PAS_ECRITE}
        </p>
      )}
    </Ligne>
  );
}
